/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { createSlice } from "@reduxjs/toolkit";
import { composerState } from "./composer.state";
import { validateModelChanged } from "./field-compare";

const composer = createSlice({
    name: "composer",

    initialState: composerState,

    reducers: {
        resetComposer (state) {
            state = composerState;
            return state;
        },
        
        initFieldState (state, action) {
            const model = action.payload;
            const state_fields = [];
            const fields = model?.fields;
            // unfortunately this is called each time we update the model
            // so only replace previous state when there is a "new" model
            const isModelChanged = validateModelChanged(state, model);
            if (!isModelChanged) {
                return;
            }
            if (Array.isArray(fields)) {
                for (let i=0; i<fields.length; i++) {
                    const field = fields[i];
                    const name = field.name;
                    const open = false;
                    const position = i+1;
                    const _field = { name, position, open };
                    state_fields.push(_field);
                }
            }
            state.guid = model?.guid;
            state.fieldsState = state_fields;
        },

        toggleFieldOpen (state, action) {
            const data = action.payload;
            const name = data?.name;
            const fields = [...state.fieldsState]; // de-proxy - immer
            const state_fields = [];
            for (const field of fields) {
                const selected = field?.name === name;
                const isOpen = selected ? !field?.open : field?.open;
                const _field = {
                    name: field?.name,
                    position: field?.position,
                    open: isOpen
                };
                state_fields.push(_field);
            }
            state.fieldsState = state_fields;
        },

        setFieldOpen (state, action) {
            const data = action.payload;
            const fields = [...state.fieldsState]; // de-proxy - immer
            const state_fields = [];
            for (const field of fields) {
                const _field = {
                    name: field?.name,
                    position: field?.position,
                    open: field?.name === data?.name
                };
                state_fields.push(_field);
            }
            state.fieldsState = state_fields;
        },

        openAllFields (state) {
            // const fields = state?.fieldsState;
            const fields = [...state.fieldsState]; // de-proxy - immer
            const state_fields = [];
            for (const field of fields) {
                const _field = {
                    name: field?.name,
                    position: field?.position,
                    open: true
                };
                state_fields.push(_field);
            }
            state.fieldsState = state_fields;
        },
        
        closeAllFields (state) {
            const fields = [...state.fieldsState]; // de-proxy - immer
            const state_fields = [];
            for (const field of fields) {
                const _field = {
                    name: field?.name,
                    position: field?.position,
                    open: false
                };
                state_fields.push(_field);
            }
            state.fieldsState = state_fields;
        },
        
        toggleComposerMode (state) {
            const isDisplay = state.mode === "display";
            const nextMode = isDisplay ? "edit" : "display";

            // inverted since we are toggling
            const nextContentOnly = !isDisplay;

            if (isDisplay) {
                state.lastComponentAdded = null;
            }

            state.contentOnly = nextContentOnly;
            state.mode = nextMode;
        },

        /**
         * @param state 
         * @param {import('@reduxjs/toolkit').PayloadAction<import('./composer.state').ComposerState['mode']>} action 
         */
        setComposerMode (state, action) {
            state.mode = action.payload;

            if (action.payload === "display") {
                state.lastComponentAdded = null;
                state.contentOnly = true;
            }
        },

        /**
         * @param state 
         * @param {import('@reduxjs/toolkit').PayloadAction<boolean>} action 
         */
        setComposerContentOnly (state, action) {
            const enabled = action?.payload;
            state.contentOnly = enabled;
        },

        /**
         * @param state 
         * @param {import('@reduxjs/toolkit').PayloadAction<string | null>} action 
         */
        setLastComponentAdded (state, action) {
            state.lastComponentAdded = action?.payload;
        },

        /**
         * @param state
         * @param {import('@reduxjs/toolkit').PayloadAction<string>} action 
         */
        setSnackbarMessage (state, action) {
            state.snackbarMessage = action?.payload;
        },

        /**
         * @param state
         * @param {import('@reduxjs/toolkit').PayloadAction<string>} action
         */
        setActiveFieldName (state, action) {
            state.activeFieldName = action?.payload;
        },
    },
});

export const composerReducer = composer.reducer;
export const {
    resetComposer,
    setComposerContentOnly,
    setComposerMode,
    setLastComponentAdded,
    toggleComposerMode,
    setSnackbarMessage,
    setActiveFieldName,
    initFieldState,
    setFieldOpen,
    toggleFieldOpen,
    openAllFields,
    closeAllFields
} = composer.actions;
